/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useCallback } from 'react'
import {
  toggleAddFileToLibrary,
  toggleAiProcessing,
  handleEvidenceLinkAdd,
  handleFileDescriptionAdd,
  removeFiles,
  resolveFiles
} from '../../../utils/files'
import { normalizeEvidence, isNewEvidence } from '@utils/survey/evidence';
import { getTableConfiguration, isSingleRowTableType, shouldEnforceWeightedAggregationTableUI } from '../../../utils/universalTracker';
import {
  getInputValue,
  getNumberScale,
  getUnitCode,
  getDefaultTableUnitOrNumberScaleCode,
} from '../../../utils/universalTrackerValue';
import {
  AssuranceState,
  ChangeQuestionProps,
  EvidenceFile,
  ExistingEvidenceFile,
  InitialResettableQuestionState,
  QuestionReducerState,
  TableDataInfo,
  ErrorMessageType,
  RowStatus,
} from '@g17eco/types/questionInterfaces';
import { tableDataToView } from '../../../utils/valueDataTable';
import { UtrvHistory } from '../../../types/universalTrackerValue';
import { UtrvPermissions } from '../../../services/permissions/UtrvPermissions';
import { EditorState } from 'lexical';
import { getDisplayCheckboxAndOriginalValueData } from './questionUtil';
import { generateDecimalErrorMessage } from '@utils/survey/input';
import { EvidenceLink, ValueTable } from '../../../types/universalTracker';
import { NewEvidenceFile, UpdateDescriptionParams } from '../../../types/file';


const assuranceState: AssuranceState = {
  assurancePortfolio: undefined,
  assurancePortfolioLoaded: false,
  hasPortfolio: false
};

// All of this state will be reset on question change
export const getInitialState = (): InitialResettableQuestionState => ({
  table: {
    editRowId: -1,
    rows: []
  },
  loaded: false,
  displayCheckbox: {},
  displayConfirmation: false,
  action: '',
  saving: false,
  errored: false,
  message: undefined,
  inputMessage: undefined,
  comments: '', // Must be string to initialize textarea as controlled comp
  editorState: undefined,
  files: [],
  existingFiles: [],
  details: {
    loaded: false,
    data: {},
  },
  canContribute: false,
  canVerify: false,
  isNA: false,
  value: undefined,
  valueData: {
    data: undefined
  },
  numberScale: undefined,
  unit: undefined,
  alternativeCode: '',
  alternativeCodeOverride: '',
});

interface ReducerAction {
  type: string;
  data?: Partial<QuestionReducerState> | { data?: UtrvHistory };
  comments?: string; // old plain text note
  editorState?: EditorState; // rich text note
  table?: Partial<TableDataInfo>;
  questionChange?: ChangeQuestionProps;
  error?: ErrorMessageType;
}

function changeQuestion(state: QuestionReducerState, { utr, utrv, surveyData, user, initiativeUtr, shouldAutoOptInToWeightedAggregation }: ChangeQuestionProps) {

  const { displayCheckbox, originalValueData } = getDisplayCheckboxAndOriginalValueData({
    valueType: utr.getValueType(),
    utrv,
  });

  const valueData = JSON.parse(JSON.stringify(originalValueData));

  const update: Partial<QuestionReducerState> = {
    valueData: valueData ?? {},
    value: getInputValue(utrv),
    unit: getUnitCode(utrv, utr, surveyData.unitConfig),
    numberScale: getNumberScale(utrv, utr, surveyData.unitConfig),
    displayCheckbox,
    canContribute: UtrvPermissions.canContribute(utrv, surveyData, user),
    canVerify: UtrvPermissions.canVerify(utrv, surveyData, user),
    alternativeCode: utr.getType(),
    alternativeCodeOverride: '',
  };

  // Retain states if we are changing to the same question
  if (state.saveId === utr.getId()) {
    update.message = state.message;
    update.saving = state.saving;
  }

  // Set up table
  const shouldEnforceTable = shouldEnforceWeightedAggregationTableUI({
    valueType: utr.getValueType(),
    aggregationValueType: utr.getAggregationValueType(),
    initiativeAggregationValueType: initiativeUtr?.aggregationValueType,
    isAutoOptIn: !!shouldAutoOptInToWeightedAggregation,
  });

  const tableConfiguration: ValueTable | undefined = getTableConfiguration(utr, shouldEnforceTable);
  if (tableConfiguration) {
    const tableData = tableDataToView(valueData);

    const unitConfig = surveyData?.unitConfig;

    // [GU-6498] Seamless conversion: when a Percentage/Number metric transitions to Table UI,
    // create an initial row from the existing value so the user sees their data in the table.
    if (shouldEnforceTable && tableData.rows.length === 0) {
      const valueColumn = tableConfiguration.valueColumn;
      const initialRowData = tableConfiguration.columns.map((col) => {
        const isValueCol = col.code === valueColumn;

        // Get default unit and scale for this column
        const defaultUnit = getDefaultTableUnitOrNumberScaleCode({
          column: col,
          unitConfig,
          key: 'unit',
        });
        const defaultNumberScale = getDefaultTableUnitOrNumberScaleCode({
          column: col,
          unitConfig,
          key: 'numberScale',
        });

        return {
          code: col.code,
          value: isValueCol ? update.value : undefined,
          unit: isValueCol ? (update.unit ?? defaultUnit) : defaultUnit,
          numberScale: isValueCol ? (update.numberScale ?? defaultNumberScale) : defaultNumberScale,
        };
      });

      // Mark as "original" so hasTableChanged (valueData.ts) won't treat this
      // conversion row as a user change when the UTRV has no stored table data.
      tableData.rows.push({
        rowStatus: RowStatus.original,
        isRemoved: false,
        id: 0,
        data: initialRowData,
      });
    }

    update.table = tableData;

    // [GU-5815] We should initialise single row validation for fresh change question,
    // to ensure that submit button is disabled when the table have validation errors.
    if ((isSingleRowTableType(utr) || shouldEnforceTable) && update.table.rows.length === 1) {
      const firstRow = update.table.rows[0];
      update.inputMessage = generateDecimalErrorMessage({
        utr: utr.getRaw(),
        input: Object.fromEntries(firstRow.data.map((c) => [c.code, c.value])),
      });
    }
  }

  return {
    ...state,
    ...getInitialState(),
    ...update,
  };
}

export function getExistingFiles(utrvHistory?: UtrvHistory): ExistingEvidenceFile[] {
  if (!utrvHistory) {
    return [];
  }

  const { stakeholderHistory, verifierHistory, documents } = utrvHistory.latestHistory;

  return [stakeholderHistory, verifierHistory].reduce<ExistingEvidenceFile[]>((acc, h) => {
    if (h) {
      const evidenceData = normalizeEvidence(h);

      for (const evidence of evidenceData) {
        const doc = documents.find((d) => d._id === evidence.documentId);
        if (doc) {
          acc.push({ ...doc, description: evidence.description, status: h.action });
        }
      }
    }

    return acc;
  }, []);
}

function loadDetails(state: QuestionReducerState, updateData: ReducerAction['data']) {

  const existingFiles = updateData && 'data' in updateData ? getExistingFiles(updateData.data) : [];

  return {
    ...state,
    details: {
      ...state.details,
      ...updateData
    },
    existingFiles,
  };
}

function toggleExistingFile(state: QuestionReducerState, data: any) {

  const { file } = data;
  const existingFiles = state.existingFiles;
  const index = existingFiles.findIndex(({ _id }) => _id === file._id);

  if (!existingFiles[index]) {
    return state;
  }

  const newFiles = [...existingFiles];
  newFiles[index].isDeleted = !newFiles[index].isDeleted;

  return { ...state, existingFiles: newFiles };
}

function updateTable(state: QuestionReducerState, data?: Partial<TableDataInfo>, error?: {[key: string]: string | undefined }) {
  if (!data) {
    return state;
  }

  return {
    ...state,
    inputMessage: error,
    table: {
      ...state.table,
      ...data
    }
  };
}

function questionReducer(state: QuestionReducerState, action: ReducerAction): QuestionReducerState {
  switch (action.type) {
    case useQuestionForm.types.handleComments:
      return { ...state, comments: action.comments || '', editorState: action.editorState }
    case useQuestionForm.types.update:
      return { ...state, ...action.data }
    case useQuestionForm.types.reset:
      return { ...getInitialState(), ...assuranceState }
    case useQuestionForm.types.changeQuestion:
      return action.questionChange ? changeQuestion(state, action.questionChange) : state;
    case useQuestionForm.types.updateExistingFiles:
      return toggleExistingFile(state, action.data);
    case useQuestionForm.types.setDetails:
      return loadDetails(state, action.data);
    case useQuestionForm.types.updateTable:
      return updateTable(state, action.table, action.error);
    default: {
      throw new Error(`Unhandled type: ${action.type}`)
    }
  }
}

export function useQuestionForm(initObj = {}, initializer?: any) {
  const [state, dispatch] = React.useReducer(
    questionReducer,
    { ...getInitialState(), ...assuranceState },
    initializer
  );

  // TODO: Update types here. Tried to use ReducerAction['data'] but it doesn't include 'files' and 'existingFiles'
  const update = useCallback((data: any) => dispatch({ type: useQuestionForm.types.update, data }), []);
  const setDetails = useCallback((data: any) => dispatch({ type: useQuestionForm.types.setDetails, data }), []);
  const resetState = useCallback(() => dispatch({ type: useQuestionForm.types.reset }), []);
  const changeQuestion = useCallback((data: ChangeQuestionProps) => dispatch({ type: useQuestionForm.types.changeQuestion, questionChange: data }), []);
  const updateTable = useCallback(
    (table: Partial<TableDataInfo>, error?: ErrorMessageType) =>
      dispatch({
        type: useQuestionForm.types.updateTable,
        table,
        error,
      }),
    []
  );
  const toggleExistingEvidence = useCallback((data: any) => dispatch({
    type: useQuestionForm.types.updateExistingFiles,
    data
  }), []);

  return {
    state,
    update,
    resetState,
    changeQuestion,
    updateTable,
    toggleExistingEvidence,
    setDetails,
    handleComments: (comments: string, editorState?: EditorState) =>
      dispatch({
        type: useQuestionForm.types.handleComments,
        comments,
        editorState,
      }),
    handleEvidenceLinkAdd: (link: EvidenceLink, isPublic: boolean) => {
      update({ files: handleEvidenceLinkAdd(state.files, link, isPublic) });
    },
    handleFilesAdded: (addFiles: File[], isExcludedFromAiProcessing = false) =>
      resolveFiles(state.files, addFiles, (newFiles: EvidenceFile[]) => {
        const markedFiles = newFiles.map((f) =>
          isNewEvidence(f) ? { ...f, excludedFromAiProcessing: isExcludedFromAiProcessing } : f
        );
        update({ files: markedFiles });
      }),
    setFiles: (addFiles: EvidenceFile[]) => {
      const newFiles = state.files.filter((file) => !('_id' in file));
      update({ files: [...newFiles, ...addFiles] });
    },
    handleFileRemoved: (index: number) => removeFiles(state.files, index, (files: EvidenceFile[]) => update({ files })),
    handleFileDescriptionAdd: ({ path, description, isUpdate }: UpdateDescriptionParams) => {
      if (isUpdate) {
        update({ existingFiles: handleFileDescriptionAdd({ evidenceFiles: state.existingFiles, path, description }) });
      }
      update({ files: handleFileDescriptionAdd({ evidenceFiles: state.files, path, description }) });
    },
    handleAddToLibrary: (file: NewEvidenceFile) =>
      update({ files: toggleAddFileToLibrary({ evidenceFiles: state.files, file }) }),
    handleAiProcessing: (file: NewEvidenceFile) =>
      update({ files: toggleAiProcessing({ evidenceFiles: state.files, file }) }),
  };
}

useQuestionForm.types = {
  handleComments: 'handleComments',
  update: 'update',
  reset: 'reset',
  changeQuestion: 'changeQuestion',
  updateExistingFiles: 'updateExistingFilesOrSo',
  setDetails: 'setDetails',
  updateTable: 'updateTable',
}
